import { cloneDeep, isEqual } from 'lodash';
import { UnwrapRef, onMounted, ref, watch } from 'vue';

import useDebounce from 'SharedDir/common/composables/useDebounce';
import { DefinedEmits, DefinedProps } from 'SharedDir/declarations/vue';

export interface PropValue<T = unknown> {
  modelValue: T;
}

export interface EmitValue<T = unknown> {
  (e: 'update:modelValue', value: T): void;
}

export interface ValueOptions<T, IT> {
  /**
   * Метод, позволяющий трансформировать значение
   * из параметра во внутренне значение компоненты
   *
   * @param value
   * @see mrofsnart
   */
  transform: (value: T) => IT;
  /**
   * То же самое что и `transform` только наоборот,
   * трансформируем внутренне значение компоненты перед событием обновления значения
   *
   * @param innerValue
   * @see transform
   */
  mrofsnart: (innerValue: IT) => T;
  onRefresh: (newValue: T, oldValue?: T) => Promise<void> | void;

  debounce?: number;

  checkEquality: boolean;

  /**
   * Не подменять внутреннее значение, если пришедшее глубоко равно текущему.
   * Эхо v-model приходит новым объектом: замена ссылки отвязывает источник данных
   * (таблица Handsontable) и заставляет перезагрузить грид.
   */
  skipEqualRefresh?: boolean;

  /**
   *  Передавать новое значение через событие изменения если метод обратной трансформации
   *  вернул константу невалидного значения
   */
  withValidation: boolean;

  /**
   * Автоматически устанавливать внутреннее значение в момент монтажа компоненты
   */
  initOnMounted: boolean;
}

class InvalidValue extends Error {}

export const useValue = <T, IT = T>(
  props: DefinedProps<PropValue<T>>,
  emits: DefinedEmits<EmitValue<T>>,
  options: Partial<ValueOptions<T, IT>> = {}
) => {
  const transform = options.transform ?? ((v) => v as unknown as IT);
  const mrofsnart = options.mrofsnart ?? ((v) => v as unknown as T);
  const checkEquality = options.checkEquality ?? false;
  const skipEqualRefresh = options.skipEqualRefresh ?? false;
  const initOnMounted = options.initOnMounted ?? true;
  const initValue = cloneDeep(transform(props.modelValue));
  const innerValue = ref<IT>(initValue);

  async function refreshInnerValue(newValue: T, oldValue?: T) {
    // console.debug('watch');
    if (!(isEqual(newValue, oldValue) && checkEquality)) {
      const transformed = transform(newValue);
      if (skipEqualRefresh && isEqual(transformed, innerValue.value)) {
        return;
      }
      innerValue.value = cloneDeep(transformed) as UnwrapRef<IT>;
      if (options.onRefresh) {
        await options.onRefresh(newValue, oldValue);
      }
    }
  }

  function initInnerValue() {
    return refreshInnerValue(props.modelValue);
  }

  if (initOnMounted) {
    onMounted(initInnerValue);
  }

  const watchHandle = watch(() => props.modelValue, refreshInnerValue, { deep: true });
  const { withDebounce } = useDebounce(options.debounce);
  const emitValue = () => {
    try {
      const value = mrofsnart(innerValue.value as IT);
      withDebounce(() => emits('update:modelValue', value));
    } catch (error) {
      if (options.withValidation && error instanceof InvalidValue) {
        console.debug(error.message);
      } else {
        throw error;
      }
    }
  };

  function dontEmitInvalidValue(message = 'Inner value is invalid') {
    throw new InvalidValue(message);
  }
  return { innerValue, emitValue, watchHandle, dontEmitInvalidValue, initInnerValue };
};
